import { useState } from "react";
import { BigNumber } from "bignumber.js";
import { useDispatch } from "react-redux";
import { get as _get } from "lodash";
import { utils as koilibUtils } from "koilib";
import { useSnackbar } from "notistack";

// Mui components
import { Button, Box, Typography, InputBase, IconButton, CircularProgress } from "@mui/material";

// Icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { numberPattern } from "../../utils/regex";
import { formatAmount } from "../../utils/format";
import { amountType } from "../../theme/main";
import DetailRow from "../DetailRow";
import { invalidateAfterTransaction } from "../../helpers/queryClient";
import { useWallet } from "../../hooks/useWallet";

// Helpers
import { calculateDefaultMana } from "../../helpers/calculate";

// Utils
import { waitTransation, describeWaitError } from "../../utils/transactions";
import { getAddressToken } from "../../utils/token";

// Actions
import { setModal } from "../../redux/actions/modals";

// Contracts
import { TokenContract, StakingContract } from "../../helpers/contracts";

// components
import TxSnackbarMessage from "../TxSnackbarMessage";
import { getErrorMessage } from "../../utils/errors";

const Staking = (props) => {
  const dispatch = useDispatch();
  const Snackbar = useSnackbar();

  // states
  const [balance, setBalance] = useState("0");
  const [loading, setLoading] = useState(false);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [inputForm, setInputForm] = useState(null);
  const [inputValue, setInputValue] = useState("0");

  // selectors
  const walletSelector = useWallet();

  // variables
  const provider = _get(walletSelector, "provider", null);
  const signer = _get(walletSelector, "signer", null);

  const approvalAllowance = async (tokenLPAddress, ownerAddress, spenderAddress, amount, configs) => {
    let _token = TokenContract(tokenLPAddress, provider, signer);
    _token.options.onlyOperation = true;
    const approvalParams = {
      owner: ownerAddress,
      spender: spenderAddress,
      value: amount,
    };
    let { operation: approveSales } = await _token.functions.approve(approvalParams, configs);
    return approveSales;
  };

  const getConfigs = async (_walletAddress) => {
    let configs = {
      payer: _walletAddress,
    };
    try {
      let account_rc = await provider.getAccountRc(_walletAddress);
      let rc_limit = calculateDefaultMana(account_rc);
      configs.rc_limit = rc_limit;
    } catch (error) {
      console.log(error);
      setLoading(false);
      Snackbar.enqueueSnackbar("Error in mana calculation", { variant: "error" });
      return;
    }
    return configs;
  };

  const finalStaking = async () => {
    setLoading(true);

    // config contract
    let walletAddress = signer.getAddress();
    let amountBal = koilibUtils.parseUnits(inputValue, 8);
    let configs = await getConfigs(walletAddress);
    let operations = [];

    // approve allowance of lp token
    try {
      let _address = props.tokenDeposit;
      let allowance = true;
      if (props.type != "farm") {
        // pools deposit a token ({ address, allowance }), farms an LP token address
        _address = await getAddressToken(_get(props, "tokenDeposit.address", ""));
        if (_get(props, "tokenDeposit.allowance") == false) {
          allowance = false;
        }
      }
      if (allowance) {
        let approvalTokenA = await approvalAllowance(_address, walletAddress, props.address, amountBal, configs);
        if (approvalTokenA) {
          operations.push(approvalTokenA);
        }
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
      Snackbar.enqueueSnackbar("Approval token a error", { variant: "error" });
      return;
    }

    // staking and unstaking
    let _contractToken = StakingContract(props.address, provider, signer);
    _contractToken.options.onlyOperation = true;
    try {
      let _params = {
        from: walletAddress,
        value: amountBal,
      };
      let result;
      if (inputForm == "STAKE") {
        result = await _contractToken.functions.deposit(_params, configs);
      } else {
        result = await _contractToken.functions.withdraw(_params, configs);
      }
      operations.push(result.operation);
    } catch (error) {
      console.log(error);
      Snackbar.enqueueSnackbar(inputForm == "STAKE" ? "Could not prepare the stake" : "Could not prepare the unstake", {
        variant: "error",
      });
      return;
    }

    let transaction = null;
    try {
      const tx = await signer.prepareTransaction({ operations: operations, header: configs });
      const { transaction: _transaction } = await signer.sendTransaction(tx);
      transaction = _transaction;
      Snackbar.enqueueSnackbar("Transaction sent", { variant: "info" });
    } catch (error) {
      console.log(error);
      Snackbar.enqueueSnackbar(getErrorMessage(error), { variant: "error" });
      setLoading(false);
      return;
    }
    // wait to be mined
    try {
      await waitTransation(provider, transaction);
    } catch (error) {
      console.log(error);
      const { message, variant } = describeWaitError(error);
      Snackbar.enqueueSnackbar(<TxSnackbarMessage message={message} txId={transaction.id} />, {
        variant,
        autoHideDuration: 15000,
      });
      clear();
      return;
    }
    clear();
    Snackbar.enqueueSnackbar(
      <TxSnackbarMessage message={inputForm == "STAKE" ? "Staked" : "Unstaked"} txId={transaction.id} />,
      {
        variant: "success",
        autoHideDuration: 10000,
      }
    );
    invalidateAfterTransaction();
  };

  const clear = () => {
    setBalance(0);
    setLoading(false);
    setLoadingBalance(false);
    setInputForm(null);
    setInputValue(null);
  };

  const selectForm = async (_form) => {
    setLoadingBalance(true);
    let _balance = "0";
    if (_form == "STAKE") {
      let addressDeposit = props.tokenDeposit;
      if (props.type != "farm") {
        addressDeposit = await getAddressToken(_get(props, "tokenDeposit.address", ""));
      }
      let _contractToken = TokenContract(addressDeposit, provider, signer);
      try {
        let result = (await _contractToken.functions.balanceOf({ owner: signer.getAddress() })).result;
        _balance = koilibUtils.formatUnits(_get(result, "value", "0"), 8);
      } catch (error) {
        console.log(error);
      }
    } else {
      _balance = koilibUtils.formatUnits(props.balance, 8);
    }
    setBalance(_balance);
    setInputForm(_form);
    setLoadingBalance(false);
  };

  const harvest = async () => {
    setLoading(true);

    // config contract
    let walletAddress = signer.getAddress();
    let configs = await getConfigs(walletAddress);
    let operations = [];

    // harvest
    let _contractToken = StakingContract(props.address, provider, signer);
    _contractToken.options.onlyOperation = true;
    console.log(_contractToken);
    try {
      let _params = {
        from: walletAddress,
      };
      let result = await _contractToken.functions.claim_pending_reward(_params, configs);
      operations.push(result.operation);
    } catch (error) {
      console.log(error);
      Snackbar.enqueueSnackbar("Error in harvest", { variant: "error" });
      return;
    }

    // transaction
    let transaction = null;
    try {
      const tx = await signer.prepareTransaction({ operations: operations, header: configs });
      const { transaction: _transaction } = await signer.sendTransaction(tx);
      transaction = _transaction;
      Snackbar.enqueueSnackbar("Transaction sent", { variant: "info" });
    } catch (error) {
      console.log(error);
      Snackbar.enqueueSnackbar(getErrorMessage(error), { variant: "error" });
      setLoading(false);
      return;
    }
    // wait to be mined
    try {
      await waitTransation(provider, transaction);
    } catch (error) {
      console.log(error);
      const { message, variant } = describeWaitError(error);
      Snackbar.enqueueSnackbar(<TxSnackbarMessage message={message} txId={transaction.id} />, {
        variant,
        autoHideDuration: 15000,
      });
      setLoading(false);
      return;
    }
    setLoading(false);
    Snackbar.enqueueSnackbar(<TxSnackbarMessage message="Harvest" txId={transaction.id} />, {
      variant: "success",
      autoHideDuration: 10000,
    });
    invalidateAfterTransaction();
  };

  const unit = props.type == "farm" ? "LP" : _get(props, "tokenDeposit.symbol", "");
  const earned =
    _get(props, "totalEarn", "0") != "0"
      ? formatAmount(koilibUtils.formatUnits(_get(props, "totalEarn", "0"), _get(props, "tokenReward.decimals", 8)), {
          maxDecimals: 6,
        })
      : "0";

  const header = (title, onBack) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
      {onBack ? (
        <IconButton aria-label="back" edge="start" onClick={onBack}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
      ) : (
        <Box sx={{ display: "flex" }}>{props.image}</Box>
      )}
      <Typography variant="h6" component="h2" id="modal-modal-title" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
        {title}
      </Typography>
      {props.onClose ? (
        <IconButton aria-label="close" edge="end" onClick={props.onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      ) : null}
    </Box>
  );

  // amount form for staking or unstaking
  if (inputForm) {
    const action = inputForm == "STAKE" ? "Stake" : "Unstake";
    return (
      <Box sx={{ p: 2.5 }}>
        {header(`${action} ${props.name}`, clear)}
        <Box sx={{ p: 2, borderRadius: "14px", backgroundColor: "background.light" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Amount
            </Typography>
            {_get(walletSelector, "wallet", null) ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Typography variant="body2" component="span" sx={{ color: "text.secondary" }}>
                  {inputForm == "STAKE" ? "Balance" : "Staked"}{" "}
                  {loadingBalance ? "…" : formatAmount(balance, { maxDecimals: 6 })}
                </Typography>
                <Button
                  variant="dark-grey-contained"
                  size="small"
                  onClick={() => numberPattern(balance, setInputValue)}
                >
                  Max
                </Button>
                <Button
                  variant="dark-grey-contained"
                  size="small"
                  sx={{ display: { xs: "none", sm: "inline-flex" } }}
                  onClick={() => {
                    numberPattern(balance, (b) => {
                      let fullAmount = koilibUtils.parseUnits(b, 8);
                      let _amount = new BigNumber(fullAmount).dividedBy(2).toFixed(0, 1);
                      let value = koilibUtils.formatUnits(_amount, 8);
                      setInputValue(value);
                    });
                  }}
                >
                  Half
                </Button>
              </Box>
            ) : null}
          </Box>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
            <InputBase
              placeholder="0"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={inputValue || ""}
              slotProps={{ input: { "aria-label": `amount to ${action.toLowerCase()}` } }}
              sx={{ ...amountType, flex: 1, minWidth: 0, fontSize: "2rem", color: "text.primary" }}
              onChange={(e) => numberPattern(e.currentTarget.value, setInputValue)}
            />
            <Typography sx={{ color: "text.secondary", fontWeight: 560 }}>{unit}</Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          size="large"
          fullWidth
          sx={{ mt: 2 }}
          disabled={
            loading || !inputValue || new BigNumber(inputValue).isZero() || !balance || new BigNumber(balance).isZero()
          }
          onClick={() => finalStaking()}
        >
          {loading ? <CircularProgress color="inherit" size={18} sx={{ mr: 1.25 }} /> : null}
          {action}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2.5 }}>
      {header(props.name)}
      {!_get(walletSelector, "wallet", null) ? (
        <>
          <Typography sx={{ color: "text.secondary", mb: 2 }}>Connect your wallet to stake in this farm.</Typography>
          <Button variant="contained" size="large" fullWidth onClick={() => dispatch(setModal("Connect"))}>
            Connect wallet
          </Button>
        </>
      ) : (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2.5 }}>
            <DetailRow
              label="Your stake"
              value={`${formatAmount(koilibUtils.formatUnits(_get(props, "balance", "0"), 8), { maxDecimals: 6 })} ${unit}`}
            />
            <DetailRow
              label="Total staked"
              value={`${formatAmount(koilibUtils.formatUnits(_get(props, "totalLiquidity", "0"), 8), { maxDecimals: 6 })} ${unit}`}
            />
            <DetailRow label="Earned" value={`${earned} ${_get(props, "tokenReward.symbol", "")}`} />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            <Button variant="contained" disabled={loading} onClick={() => selectForm("STAKE")}>
              Stake
            </Button>
            <Button variant="outlined" disabled={loading} onClick={() => selectForm("UNSTAKE")}>
              Unstake
            </Button>
            <Button
              variant="outlined"
              sx={{ gridColumn: "1 / -1" }}
              disabled={loading || new BigNumber(_get(props, "totalEarn", "0")).isZero()}
              onClick={() => harvest()}
            >
              {loading ? <CircularProgress color="inherit" size={18} sx={{ mr: 1.25 }} /> : null}
              Harvest rewards
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default Staking;
